import { AnimatePresence } from "framer-motion";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import useBarcodeScanner from "./../hooks/useBarcodeScanner";
import { AgeVerificationDialog } from "./AgeVerificationDialog";
import { Product } from "./Product";
import { ProductAccessRow } from "./ProductAccessRow";
import { KitchenPromoBanner, PromoBanner } from "./Promotion";
import kitchenMenuLogo from "../../../assets/kitchen-menu.svg";
import { ActionKind } from "../../types/Action";
import { FeaturedKitchenProduct } from "../../types/FeaturedProduct";
import * as Price from "../../utils/Price";
import { translate } from "../../utils/translations";
import { Button } from "../Button";
import { CartErrorDialog } from "../CartErrorDialog";
import { CheckoutDialog } from "../CheckoutDialog";
import { useConfig } from "../ConfigProvider";
import { useOnScanProduct } from "../hooks/useOnScanProduct";
import { Kitchen } from "../kitchen/Kitchen";
import { usePriceFormat } from "../PriceFormatProvider";
import { ProductPicker } from "../productPicker/ProductPicker";
import { SpinnerOverlay } from "../Spinner";
import {
    RootStateKind,
    ScanSubStateKind,
    useRootState
} from "../state/StateProvider";
import * as svg from "../svg";
import { useDelayedCondition } from "../useDelayedCondition";

const CartComponent = () => {
    const { t, i18n } = useTranslation();

    const { state, dispatch } = useRootState(RootStateKind.Scan);
    const isLoading = state.subState.kind === ScanSubStateKind.Loading;
    const { accessibilityMode, kitchen } = state;

    const displayLoadingIndicator = useDelayedCondition(isLoading, 2000);
    const { featuredProducts } = useConfig();

    const featuredKitchenProducts: FeaturedKitchenProduct[] =
        state.kitchen.hasKitchen && state.kitchen.menu
            ? state.kitchen.menu?.categories
                  .flatMap((c) =>
                      c.products.filter(
                          (p) => p.isPromoted && p.price && p.available
                      )
                  )
                  .map((p) => ({
                      name: translate(p.name, i18n),
                      imageUrl: p.imageUrl ?? "",
                      barcode: p.barcode,
                      price: {
                          currency: p.price!.currency,
                          value: p.price!.value
                      }
                  }))
            : [];

    const format = usePriceFormat();

    const { cart } = state;

    const grossPrice = format(cart.grossPrice);
    const totalPrice = format(cart.totalPrice);

    const productImages = kitchen.hasKitchen
        ? kitchen.menu?.categories.flatMap(({ products }) =>
              products.map((p) => p.imageUrl)
          )
        : undefined;

    const promos = (
        <div className="grid grid-cols-2 gap-2">
            {featuredKitchenProducts.map((p) => (
                <KitchenPromoBanner key={p.barcode} product={p} />
            ))}
            {featuredProducts.map((p) => (
                <PromoBanner key={p.barcode} promotionalProduct={p} />
            ))}
        </div>
    );

    const Subtitle = ({ children }: PropsWithChildren) => (
        <div className="w-60 flex-wrap text-center text-sm font-semibold text-black">
            {children}
        </div>
    );

    const kitchenDescription = (
        <div
            className={`flex h-full justify-center ${
                accessibilityMode ? "gap-2" : "flex-col gap-5"
            }`}
        >
            <div className="flex flex-col items-center justify-center gap-4">
                <div className="text-primary-500">{svg.scan}</div>
                <Subtitle>{t("scan")}</Subtitle>
            </div>
            {accessibilityMode ? (
                <hr className="mx-auto my-0 h-full w-0.5 bg-gray-200" />
            ) : (
                <hr className="mx-auto my-0 h-0.5 w-5/12" />
            )}
            <div className="flex flex-col items-center justify-center gap-4">
                <Button
                    className="h-24"
                    onClick={() => {
                        if (kitchen.hasKitchen) {
                            dispatch({
                                kind: ActionKind.ShowKitchenMenu
                            });
                        }
                    }}
                    disabled={!kitchen.hasKitchen || !kitchen.menu}
                >
                    <img
                        src={kitchenMenuLogo}
                        className="mx-auto h-16 w-auto object-contain"
                    />
                </Button>
                <Subtitle>{t("kitchenDescription")}</Subtitle>
            </div>
        </div>
    );

    return (
        <>
            <div data-testid="cartComponent" className="flex h-full flex-col">
                {productImages?.map((image, i) => (
                    <link key={i} rel="preload" as="image" href={image} />
                ))}
                {displayLoadingIndicator && <SpinnerOverlay />}
                <div
                    className={`flex flex-[1_0px] flex-col overflow-y-auto px-3 ${
                        accessibilityMode && "h-24"
                    }`}
                >
                    {cart.positions.length === 0 ? (
                        <>
                            {kitchen.hasKitchen ? (
                                kitchenDescription
                            ) : (
                                <div className="flex flex-auto flex-col items-center justify-center gap-10">
                                    <div className="text-primary-500">
                                        {svg.scan}
                                    </div>
                                    <Subtitle>{t("scan")}</Subtitle>
                                </div>
                            )}
                        </>
                    ) : (
                        <AnimatePresence>
                            {cart.positions.map((position, index) => (
                                <Product
                                    key={`position-${index}`}
                                    position={position}
                                />
                            ))}
                        </AnimatePresence>
                    )}
                </div>

                <div
                    className={`flex flex-col gap-3 border-primary-200 p-3 ${cart.positions.length > 0 ? "border-t-2" : ""} `}
                >
                    {featuredProducts.length + featuredKitchenProducts.length >
                        0 && promos}

                    <div className="font-bold text-primary-800">
                        {t("total")}

                        <div className="float-right">
                            {grossPrice !== totalPrice && (
                                <span className="mr-2 text-slate-500 line-through">
                                    {grossPrice}
                                </span>
                            )}
                            <span>{totalPrice}</span>
                        </div>
                    </div>

                    <Button
                        variant="primary"
                        disabled={Price.isZero(cart.grossPrice) || isLoading}
                        data-testid="toPaymentButton"
                        onClick={() =>
                            dispatch({ kind: ActionKind.ShowCheckoutDialog })
                        }
                        className="flex flex-col items-center py-2 text-center leading-tight"
                    >
                        <span className="font-bold">{t("toPayment")}</span>
                        <span className="text-xs opacity-80">{totalPrice}</span>
                    </Button>

                    <ProductAccessRow />
                </div>
            </div>

            <AgeVerificationDialog />
            <CheckoutDialog />
        </>
    );
};

export const SelfCheckout = () => {
    const { state, dispatch } = useRootState(RootStateKind.Scan);

    const { subState } = state;

    const onScan = useOnScanProduct();
    useBarcodeScanner({ onScan });

    return (
        <>
            {subState.kind === ScanSubStateKind.Error && (
                <CartErrorDialog
                    error={subState.reason}
                    onOk={() =>
                        dispatch({ kind: ActionKind.HideCartErrorDialog })
                    }
                />
            )}
            <CartComponent />
            <ProductPicker />
            {state.kitchen.hasKitchen && state.kitchen.menu && <Kitchen />}
        </>
    );
};
